/**
 * ═══════════════════════════════════════════════════
 * EUROBASED - CENTRAL CART BUBBLE MANAGER
 * Zentralisierte Cart-Bubble-Logik für gesamte Site
 * Version: 1.0
 * Stand: 25.01.2026
 * ═══════════════════════════════════════════════════
 */

class CartBubbleManager {
  constructor() {
    this.bubble = null;
    this.isInitialized = false;
  }

  /**
   * Initialisiert die Cart-Bubble und Event-Listener
   */
  init() {
    if (this.isInitialized) return;

    const cartIcon = document.querySelector('[aria-controls="cart-drawer"], a[href="/cart"]');
    if (!cartIcon) return;

    const cartContainer = cartIcon.closest('a') || cartIcon;
    if (getComputedStyle(cartContainer).position === 'static') {
      cartContainer.style.position = 'relative';
    }

    let bubble = cartContainer.querySelector('.cart-bubble');
    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'cart-bubble';
      bubble.setAttribute('aria-live', 'polite');
      bubble.setAttribute('aria-atomic', 'true');
      cartContainer.appendChild(bubble);
    }

    this.bubble = bubble;
    this.isInitialized = true;

    // Initial update
    this.update(false);

    // Event-Listener
    document.addEventListener('cart:refresh', () => this.update(true));
    document.addEventListener('cart:updated', () => this.update(true));
    document.addEventListener('theme:cart:change', () => this.update(true));

    // Theme cart:change abfangen und Smart-Count korrigieren
    document.addEventListener('cart:change', () => {
      // Kurz warten bis Theme-CartCount seinen Wert gesetzt hat, dann überschreiben
      setTimeout(() => this.updateAllCartElements(), 50);
    });

    // Mutation Observer
    this.observeCartChanges();
  }

  /**
   * Aktualisiert Cart-Bubble mit aktuellem Count
   * @param {boolean} animate - Ob Pop-Animation gezeigt werden soll
   */
  async update(animate = false) {
    if (!this.bubble) return;

    try {
      const count = await this.fetchCartCount();
      const currentCount = parseInt(this.bubble.textContent) || 0;

      // Verhindere Ausblenden bei 0, wenn vorher Wert vorhanden war
      if (count === 0 && currentCount > 0) return;

      if (count > 0) {
        this.bubble.textContent = count;
        this.bubble.classList.add('is-visible');
        if (animate && count !== currentCount) {
          this.triggerPopAnimation();
        }
      } else {
        this.bubble.classList.remove('is-visible');
      }
    } catch (error) {
      // Fallback: Behalte aktuellen Count
      const currentCount = parseInt(this.bubble.textContent) || 0;
      if (currentCount > 0) {
        this.bubble.classList.add('is-visible');
      }
    }
  }

  /**
   * Holt aktuelle Cart-Item-Count von Shopify
   * BYO-Bundles (gleiche _config_id) zählen als 1 Produkt
   * @returns {Promise<number>}
   */
  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return 0;
      const cart = await response.json();
      return this.computeSmartCount(cart);
    } catch {
      return 0;
    }
  }

  /**
   * Berechnet Smart-Count: BYO-Bundles als 1 Produkt
   * @param {Object} cart - Cart-Objekt von /cart.js
   * @returns {number}
   */
  computeSmartCount(cart) {
    if (!cart || !cart.items) return 0;

    const configIds = new Set();
    let normalCount = 0;
    const packagingHandle = 'versandverpackung-aufschlag';

    cart.items.forEach(item => {
      if (item.handle === packagingHandle) return;
      if (item.properties?._is_surcharge === 'true') return;
      const configId = item.properties?._config_id;
      if (configId) {
        configIds.add(configId);
      } else {
        normalCount += item.quantity || 1;
      }
    });

    return normalCount + configIds.size;
  }

  /**
   * Triggert Pop-Animation
   */
  triggerPopAnimation() {
    if (!this.bubble) return;

    this.bubble.classList.remove('is-popping');
    void this.bubble.offsetWidth; // Force reflow
    this.bubble.classList.add('is-popping');
    setTimeout(() => this.bubble.classList.remove('is-popping'), 500);
  }

  /**
   * Beobachtet Cart-Drawer für Änderungen
   */
  observeCartChanges() {
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');

    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        const shouldUpdate = mutations.some(mutation =>
          mutation.type === 'childList' || mutation.type === 'characterData'
        );
        if (shouldUpdate) this.update(true);
      });

      observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Form-Submit für /cart/add beobachten
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        setTimeout(() => this.update(true), 500);
      }
    });
  }

  /**
   * Aktualisiert alle Cart-Count-Elemente auf der Seite
   * Kompatibilität mit verschiedenen Selektoren + Theme cart-count
   */
  async updateAllCartElements() {
    try {
      const count = await this.fetchCartCount();

      // Update alle möglichen Cart-Count-Elemente (inkl. Theme cart-count)
      const selectors = '.cart-count, [data-cart-count], .cart-bubble, cart-count';
      const cartCounts = document.querySelectorAll(selectors);

      cartCounts.forEach(el => {
        const visibleCount = el.querySelector('[aria-hidden="true"]');
        if (visibleCount) {
          visibleCount.textContent = count;
        } else {
          el.textContent = count;
        }

        if (count > 0) {
          el.classList.add('is-visible');
          el.classList.remove('opacity-0');
          if (el.style) el.style.display = '';
        } else {
          el.classList.remove('is-visible');
        }
      });

      return count;
    } catch (error) {
      console.error('[CartManager] Failed to update cart elements:', error);
      return 0;
    }
  }

  /**
   * Triggert Pop-Effekt auf allen Cart-Elementen
   */
  triggerCartPopEffect() {
    const cartElements = document.querySelectorAll(
      '.header__cart, [href="/cart"], .cart-bubble, .cart-icon'
    );

    cartElements.forEach(el => {
      el.classList.add('cart-pop-effect');
      setTimeout(() => el.classList.remove('cart-pop-effect'), 600);
    });
  }
}

// Singleton-Instanz
const cartManager = new CartBubbleManager();

// Export für globale Verwendung
window.CartBubbleManager = cartManager;

/* ═══════════════════════════════════════════════════
   SHARED POST-ADD HANDLER — kein Redirect mehr
   Wird von ALLEN Custom-Add-to-Cart-Pfaden aufgerufen statt window.location.href='/cart'.
   Desktop: „+1"-Bubble (oben am Warenkorb) + Toast (cart-success-toast feuert via fetch).
   Mobile:  „+1"-Bubble + der native <cart-drawer> fährt raus (Toast wird mobil unterdrückt).
   ═══════════════════════════════════════════════════ */
window.ebAfterAdd = function ebAfterAdd() {
  // 1) „+1"-Mikrointeraktion am Warenkorb-Icon (beide Geräte) — erprobtes Muster
  try {
    if (window.CartBubbleManager) {
      window.CartBubbleManager.update(true);
      window.CartBubbleManager.triggerCartPopEffect();
    }
  } catch (e) {}

  // 2) Theme-nativ: lässt den <cart-drawer> seinen Inhalt selbst neu rendern
  //    (CartDrawer._onCartRefresh holt /?section_id=cart-drawer) + aktualisiert die Bubble.
  try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}

  // 3) Mobile: den Cart-Drawer als Bestätigung aufschieben (Desktop behält den Toast).
  try {
    if (window.matchMedia && window.matchMedia('(max-width: 699px)').matches) {
      var drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.show === 'function') {
        // Bestaetigungs-Chip "Hinzugefuegt" nur im After-Add-Kontext zeigen;
        // Klasse faellt beim Schliessen wieder ab (Header-Icon-Oeffnung = neutraler Titel)
        drawer.classList.add('is-after-add');
        var clearAfterAdd = function () { drawer.classList.remove('is-after-add'); };
        drawer.addEventListener('dialog:after-hide', clearAfterAdd, { once: true });
        drawer.addEventListener('close', clearAfterAdd, { once: true });
        // kurze Verzögerung, damit der cart:refresh den Inhalt erst befüllt
        setTimeout(function () { try { drawer.show(); } catch (e) {} }, 300);
      }
    }
  } catch (e) {}
};

// Auto-Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => cartManager.init());
} else {
  // DOM ist bereits geladen, init mit Verzögerung
  setTimeout(() => cartManager.init(), 100);
}

/* ═══════════════════════════════════════════════════
   CART-DRAWER: Inhalt faehrt MIT dem Panel auf.
   Die Theme-Timeline (theme.js _showTransition) blendet die Shadow-Kinder
   erst NACH der 0.4s-Clip-Animation ein -> ~0.55s leeres Navy-Panel beim
   Oeffnen (Owner-Video 09.07.). Der Override laeuft NUR fuer #cart-drawer
   (Prototyp der cart-drawer-Klasse), Burger/Suche behalten das Original.
   ═══════════════════════════════════════════════════ */
(function () {
  'use strict';
  function patchen() {
    var el = document.getElementById('cart-drawer');
    if (!el || !el.shadowRoot) { return false; }
    var proto = Object.getPrototypeOf(el);
    if (!proto || proto.__ebParallelShow || typeof proto._showTransition !== 'function') { return true; }
    proto.__ebParallelShow = true;
    proto._showTransition = function (animieren) {
      var self = this;
      if (typeof this._setInitialPosition === 'function') this._setInitialPosition();
      var reduziert = (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        || (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.reduceDrawerAnimation);
      var faktor = animieren === false ? 0 : 1;
      var content = this.shadowRoot.querySelector('[part="content"]');
      var schliesser = this.shadowRoot.querySelector('[part="outside-close-button"]');
      var anims = [];
      function lauf(ziel, frames, opt) {
        if (!ziel || !ziel.animate) return;
        /* fill:forwards bleibt bewusst stehen (kein commitStyles/cancel —
           commitStyles wirft bei nicht gerendertem Fenster, cancel liesse den
           Inhalt dann wieder auf opacity 0 fallen). Die Original-Hide-Animation
           gewinnt als spaetere Animation ohnehin. */
        anims.push(ziel.animate(frames, opt));
      }
      lauf(this, { opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 150 * faktor, fill: 'forwards' });
      if (content && !reduziert && typeof this._getClipPathProperties === 'function') {
        lauf(content, { clipPath: this._getClipPathProperties() }, { duration: 400 * faktor, easing: 'cubic-bezier(0.86, 0, 0.07, 1)', fill: 'forwards' });
        Array.prototype.forEach.call(content.children, function (kind) {
          lauf(kind, { opacity: [0, 1] }, { duration: 300 * faktor, fill: 'forwards' });
        });
      }
      lauf(schliesser, { opacity: [0, 1] }, { duration: 150 * faktor, fill: 'forwards' });
      return Promise.all(anims.map(function (a) { return a.finished.catch(function () {}); }))
        .then(function () { self.classList.add('show-close-cursor'); });
    };
    return true;
  }
  // custom element ist evtl. noch nicht upgegradet -> kurz nachfassen
  if (!patchen()) {
    var versuche = 0;
    var t = setInterval(function () { if (patchen() || ++versuche > 20) clearInterval(t); }, 250);
  }
})();

